import i18next from 'i18next';
import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import React from 'react';

import { GlobalSearchIcon, PixelIcon } from '~/assets';
import { CustomerListIcon, MatchbackIcon, SegmentsIcon } from '~/assets/audience';
import LookalikeIcon from '~/assets/audience/LookalikeIcon';
import PulseIcon from '~/assets/audience/PulseIcon';
import { AudienceNode, User } from '~/constants/graphqlTypes';
import { TaxonomyProperty } from '~/contexts/AudienceContext';
import {
  AudienceCondition,
  AudienceCustomerListInfo,
  AudienceFilterDateField,
  AudienceFilterNumberField,
  AudienceGlobalInfo,
  AudienceOverviewQuery,
  AudiencePixelStatus,
  AudiencePulseInfo,
  BaseAdditionsArrayInfo,
  CustomerListInfoFieldsFragment,
  CustomerListMatchingAttribute,
  FilterExtendedStringField,
  FilterNumberField,
  FilterStringField,
  LogicalOperator,
  PersonFilterInput,
  PixelMatchingCadence,
  UtmGroup,
} from '~/types/api.graphql';
import { getMatchingAttributeLabel } from '~/utils/customerListMatchingRules';

import { MatchTypeValue } from '../components/MatchTypeDropdown/MatchTypeDropdown';
import { visitIQTaxonomyUserSettings } from '../constants/localStorage';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

export const allConditionsArray = [
  AudienceCondition.Pixel,
  AudienceCondition.CustomerList,
  AudienceCondition.Global,
  AudienceCondition.Segment,
  AudienceCondition.Lookalike,
  AudienceCondition.Pulse,
];

export const allPixelStatuses = [
  AudiencePixelStatus.Enabled,
  AudiencePixelStatus.Disabled,
  AudiencePixelStatus.QuotaReached,
  AudienceFilterNumberField.QuotaUsagePercentage,
  AudienceFilterDateField.LastNewMatchDate,
];

export const conditionIconsMap: Record<AudienceCondition, JSX.Element | null> = {
  [AudienceCondition.Pixel]: <PixelIcon />,
  [AudienceCondition.Global]: <GlobalSearchIcon />,
  [AudienceCondition.Segment]: <SegmentsIcon />,
  [AudienceCondition.CustomerList]: <CustomerListIcon />,
  [AudienceCondition.Matchback]: <MatchbackIcon />,
  [AudienceCondition.Lookalike]: <LookalikeIcon />,
  [AudienceCondition.Pulse]: <PulseIcon />,
};

export interface AudienceConditionFilter {
  id: AudienceCondition;
  text: string;
}

export enum GroupKey {
  Custom = 'custom',
  B2B = 'b2b',
  ConsumerProfile = 'consumerProfile',
  Demographics = 'demographics',
  Household = 'household',
  Financial = 'financial',
}
type PropertyGroup = {
  key: GroupKey;
  label: string;
  properties: TaxonomyProperty[];
};

type AudienceInfoWithAdditions = AudiencePulseInfo | AudienceGlobalInfo | AudienceCustomerListInfo;

const PROPERTY_GROUPS_DEFINITIONS = [
  {
    key: GroupKey.Custom,
    label: 'Custom',
    properties: [], // Will be loaded from localStorage/user
  },
  {
    key: GroupKey.B2B,
    label: 'B2B',
    properties: [
      FilterStringField.JobTitle,
      FilterStringField.Department,
      FilterStringField.PrimaryIndustry,
      FilterStringField.CompanyState,
      FilterStringField.CompanyRevenue,
      FilterStringField.CompanyEmployeeCount,
    ],
  },
  {
    key: GroupKey.ConsumerProfile,
    label: 'Consumer Profile',
    properties: [
      FilterStringField.Interests,
      FilterStringField.Behaviors,
      FilterStringField.State,
      FilterStringField.Language,
      FilterStringField.Generation,
    ],
  },
  {
    key: GroupKey.Demographics,
    label: 'Demographics',
    properties: [
      FilterNumberField.Age,
      FilterStringField.Gender,
      FilterStringField.EthnicGroup,
      FilterStringField.Education,
      FilterStringField.MaritalStatus,
    ],
  },
  {
    key: GroupKey.Household,
    label: 'Household',
    properties: [
      FilterStringField.HomeOwner,
      FilterStringField.IncomeLevels,
      FilterStringField.NetWorth,
      FilterNumberField.PeopleInHousehold,
      FilterStringField.DwellingType,
    ],
  },
  {
    key: GroupKey.Financial,
    label: 'Financial',
    properties: [
      FilterStringField.CreditRange,
      FilterStringField.NetWorth,
      FilterNumberField.MortgageAmount,
      FilterStringField.NewCreditOfferedHousehold,
    ],
  },
];

export const conditionIcon = (condition: AudienceCondition) => conditionIconsMap[condition] || <GlobalSearchIcon />;

export const conditionTitle = (condition: AudienceCondition) =>
  i18next.exists(`specific:audience.overview.${condition}`)
    ? i18next.t(`specific:audience.overview.${condition}`)
    : condition;

export const conditionHeaderTitle = (condition: AudienceCondition) =>
  i18next.exists(`specific:audience.overview.${condition}HeaderTitle`)
    ? i18next.t(`specific:audience.overview.${condition}HeaderTitle`)
    : condition;

/**
 * @param condition: AudienceCondition
 * @returns the tooltip content for a given audience condition, or null if the tooltip content does not exist (e.g. no translation key defined).
 */
export const conditionTypeTooltip = (condition: AudienceCondition) =>
  i18next.exists(`specific:audience.overview.${condition}Tooltip`)
    ? i18next.t(`specific:audience.overview.${condition}Tooltip`)
    : null;

type AudienceWithCondition = Pick<AudienceNode, 'condition' | 'parentAudience'>;

export const shouldShowPixelComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Pixel || audience?.parentAudience?.condition === AudienceCondition.Pixel;

export const shouldShowGlobalComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Global || audience?.parentAudience?.condition === AudienceCondition.Global;

export const shouldShowPulseComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.Pulse || audience?.parentAudience?.condition === AudienceCondition.Pulse;

export const shouldShowCustomerListComponents = (audience?: AudienceWithCondition) =>
  audience?.condition === AudienceCondition.CustomerList ||
  audience?.parentAudience?.condition === AudienceCondition.CustomerList;

export const shouldShowCompoundDateAdditionsFilter = (audience?: AudienceWithCondition) =>
  shouldShowGlobalComponents(audience) ||
  shouldShowCustomerListComponents(audience) ||
  shouldShowPulseComponents(audience);

export const shouldShowDateFilter = (audience?: AudienceWithCondition) => shouldShowPixelComponents(audience);

export const getAudienceAdditions = (audience?: AudienceOverviewQuery['audience']) => {
  if (audience?.condition === AudienceCondition.Segment) {
    return (audience?.parentAudience?.info as AudienceInfoWithAdditions)?.additions || [];
  }
  return (audience?.info as AudienceInfoWithAdditions)?.additions || [];
};

type MatchingRulesInfo = Pick<CustomerListInfoFieldsFragment, 'matchingRulesEnabled' | 'matchingRules'>;

/**
 * The matching rules this audience's people can be shown and filtered by, or null when the audience doesn't support
 * them: a Customer List that records rule provenance, a Matchback that saved rules at creation, or a Segment of such
 * a Customer List (it shows and filters by its parent's rules; a Segment of a Matchback is rejected by the API).
 */
export const getAudienceMatchingRules = (audience?: AudienceOverviewQuery['audience']) => {
  if (audience?.condition === AudienceCondition.Segment) {
    return audience.parentAudience?.condition === AudienceCondition.CustomerList
      ? getParentMatchingRules(audience)
      : null;
  }
  if (audience?.condition !== AudienceCondition.CustomerList && audience?.condition !== AudienceCondition.Matchback) {
    return null;
  }
  const info = audience.info as MatchingRulesInfo;
  return info?.matchingRulesEnabled ? info.matchingRules ?? [] : null;
};

const getParentMatchingRules = (audience: NonNullable<AudienceOverviewQuery['audience']>) => {
  const parentInfo = audience.parentAudience?.info as MatchingRulesInfo;
  return parentInfo?.matchingRulesEnabled ? parentInfo.matchingRules ?? [] : null;
};

/**
 * Like `getAudienceMatchingRules`, for what a download or activation can include. Exports of a Segment read its parent
 * Customer List's rules whatever the parent's condition says, as before.
 */
export const getExportMatchingRules = (audience?: AudienceOverviewQuery['audience']) => {
  if (audience?.condition !== AudienceCondition.Segment) return getAudienceMatchingRules(audience);
  return getParentMatchingRules(audience);
};

/** A matching rule as people read it: its attributes joined, e.g. "First Name + Last Name + ZIP". */
export const getMatchingRuleLabel = (fields: { attribute: CustomerListMatchingAttribute }[]) =>
  fields.map(({ attribute }) => getMatchingAttributeLabel(attribute)).join(' + ');

export const dateFilterFromDateRange = (dateRange: DateRange, matchType: MatchTypeValue) => {
  return {
    dateFilter: {
      field: matchType,
      operator: { between: { from: getUtcDateString(dateRange.start), to: getUtcDateString(dateRange.end) } },
    },
  };
};

export interface AttributionSource {
  group: UtmGroup;
  name: string;
}

/**
 * Creates a filter for a single param value.
 * If the value contains spaces, creates a compound OR filter to match both
 * %20 and + encoded variations (since URL spaces can be encoded either way).
 */
const createParamFilter = (param: string, value: string): PersonFilterInput => {
  const trimmedValue = value.trim();
  const hasSpace = trimmedValue.includes(' ');

  if (!hasSpace) {
    return {
      extendedStringFilter: {
        field: FilterExtendedStringField.PixelHitUrl,
        operator: { contains: `${param}=${encodeURIComponent(trimmedValue)}` },
      },
    };
  }

  // Value has spaces - search for both %20 and + variations
  const encodedWithPercent = `${param}=${encodeURIComponent(trimmedValue)}`; // spaces become %20
  const encodedWithPlus = `${param}=${trimmedValue.replace(/ /g, '+')}`; // spaces become +

  return {
    compoundFilter: {
      operator: LogicalOperator.Or,
      filters: [
        {
          extendedStringFilter: {
            field: FilterExtendedStringField.PixelHitUrl,
            operator: { contains: encodedWithPercent },
          },
        },
        {
          extendedStringFilter: {
            field: FilterExtendedStringField.PixelHitUrl,
            operator: { contains: encodedWithPlus },
          },
        },
      ],
    },
  };
};

export const attributionSourceFilters = (attributionSource: AttributionSource): PersonFilterInput[] => {
  if (!attributionSource) return [];

  const mapAttributions: Record<string, string> = {
    [UtmGroup.Source]: 'utm_source',
    [UtmGroup.Campaign]: 'utm_campaign',
    [UtmGroup.Medium]: 'utm_medium',
  };

  if (attributionSource.group !== UtmGroup.SourceAndMedium) {
    // Single param - return as single filter
    return [createParamFilter(mapAttributions[attributionSource.group], attributionSource.name)];
  }

  // SourceAndMedium - return source and medium as separate filters
  // FilterContext extracts compound filters as separate groups and combines with AND
  const names = attributionSource.name.split('/');
  if (names.length !== 2) return [];

  return [
    createParamFilter(mapAttributions[UtmGroup.Source], names[0]),
    createParamFilter(mapAttributions[UtmGroup.Medium], names[1]),
  ];
};

const audiencesConditionsThatSupportLookalikes = [
  AudienceCondition.CustomerList,
  AudienceCondition.Pixel,
  AudienceCondition.Pulse,
];

export const shouldShowLookalikes = (audienceData: AudienceWithCondition | AudienceOverviewQuery['audience']) =>
  audiencesConditionsThatSupportLookalikes.includes(audienceData?.condition) ||
  audiencesConditionsThatSupportLookalikes.includes(audienceData?.parentAudience?.condition);

export const getRangeDefault = (audienceData: AudienceOverviewQuery) => {
  // don't use default date for Global audiences or Customer List audiences
  if (shouldShowGlobalComponents(audienceData?.audience) || shouldShowCustomerListComponents(audienceData?.audience))
    return undefined;

  if (shouldShowPixelComponents(audienceData?.audience)) {
    return moment.range(moment().subtract(27, 'days'), moment());
  }

  const audienceAdditions = getAudienceAdditions(audienceData?.audience);
  const lastAddition = audienceAdditions?.at(-1);
  if (lastAddition) {
    return moment.range(moment(lastAddition.date).startOf('day'), moment(lastAddition.date).endOf('day'));
  }

  return undefined;
};

export interface ChartTypesByGroup {
  [groupKey: string]: { [propertyKey: string]: string };
}
// UserTaxonomySettingsManager: manages user-global taxonomy settings in localStorage
export type UserTaxonomySettings = {
  chartTypesByGroup: ChartTypesByGroup;
};

const getUserTaxonomySettingsKey = (userId: string) => `${visitIQTaxonomyUserSettings}-${userId}`;

export const UserTaxonomySettingsManager = {
  load(userId: string): UserTaxonomySettings {
    const raw = localStorage.getItem(getUserTaxonomySettingsKey(userId));
    if (!raw) {
      return { chartTypesByGroup: {} };
    }
    try {
      return JSON.parse(raw);
    } catch {
      return { chartTypesByGroup: {} };
    }
  },
  save(userId: string, settings: UserTaxonomySettings) {
    localStorage.setItem(getUserTaxonomySettingsKey(userId), JSON.stringify(settings));
  },

  getChartType(userId: string, groupKey: string, propertyKey: string): string | undefined {
    return this.load(userId).chartTypesByGroup?.[groupKey]?.[propertyKey];
  },
  setChartType(userId: string, groupKey: string, propertyKey: string, chartType: string) {
    const settings = this.load(userId);
    if (!settings.chartTypesByGroup[groupKey]) settings.chartTypesByGroup[groupKey] = {};
    settings.chartTypesByGroup[groupKey][propertyKey] = chartType;
    this.save(userId, settings);
  },

  getPropertiesByGroup(personProperties?: TaxonomyProperty[]): PropertyGroup[] {
    return PROPERTY_GROUPS_DEFINITIONS.map((group) => {
      return {
        ...group,
        properties: personProperties.filter((property) => {
          // Check if the property is in the group's properties array
          return group.properties.includes(property.key as FilterStringField | FilterNumberField);
        }),
      };
    });
  },
};

export const DUMMY_DAYS_TO_KEEP = 1;
export const DUMMY_HISTORICAL_DATA_SYNC = false;

export const shouldShowDaysToKeep = (audienceData: AudienceOverviewQuery) => {
  const isSegment = audienceData?.audience?.condition === AudienceCondition.Segment;
  const audienceCondition = audienceData?.audience?.condition;
  const parentCondition = audienceData?.audience?.parentAudience?.condition;
  const conditionToCheck = isSegment ? parentCondition : audienceCondition;
  const conditionsNotAllowingDaysToKeep = [AudienceCondition.CustomerList, AudienceCondition.Lookalike];
  return !conditionsNotAllowingDaysToKeep.includes(conditionToCheck);
};

export const pixelCadenceToString = (pixelCadence: PixelMatchingCadence) => {
  if (pixelCadence === PixelMatchingCadence.FourHours) {
    return i18next.t('common.every4Hours');
  }
  return i18next.t('common.every24Hours');
};

export const shouldHideAdditionCount = (audienceData: AudienceOverviewQuery) => {
  const audienceCondition = audienceData?.audience?.condition;
  if (audienceCondition === AudienceCondition.Segment) {
    return true;
  }
  const additions = getAudienceAdditions(audienceData?.audience);
  if (!additions?.length) {
    return false;
  }
  // If an audience can have duplicate people between different additions, we can have a day with multiple additions,
  // where people ends up being counted multiple times.
  // That's why we hide the addition count in those scenarios.
  const conditionsAllowingDuplicatesBetweenAdditions = [AudienceCondition.CustomerList, AudienceCondition.Pulse];
  if (conditionsAllowingDuplicatesBetweenAdditions.includes(audienceCondition)) {
    const additionsByDay: Record<string, number> = {};
    for (const { date } of additions) {
      if (!date) continue;
      const dayKey = new Date(date as string).toISOString().split('T')[0];
      additionsByDay[dayKey] ||= 0;
      if (++additionsByDay[dayKey] > 1) {
        return true;
      }
    }
  }
  return false;
};

const CREATE_PATH_BY_CONDITION: Record<AudienceCondition, string | null> = {
  [AudienceCondition.Pulse]: '/app/audiences/create-pulse',
  [AudienceCondition.Pixel]: '/app/audiences/create-pixel',
  [AudienceCondition.Global]: '/app/audiences/create-global-search',
  [AudienceCondition.Lookalike]: '/app/audiences/create-lookalike',
  [AudienceCondition.Matchback]: '/app/audiences/create-matchback',
  [AudienceCondition.CustomerList]: '/app/audiences/create-customer-list',
  [AudienceCondition.Segment]: '/app/audiences/create-segment',
};

export const getCreatePathForCondition = (condition: AudienceCondition): string | null => {
  return CREATE_PATH_BY_CONDITION[condition];
};

export const canCreateAudienceType = (
  condition: AudienceCondition,
  me: User | undefined,
  hasOrgWithSubscriptionOverride: boolean,
  hasOrgWithPaidSubscription: boolean,
  isOrgAdmin: boolean,
): boolean => {
  if (!me) return false;

  const isGlobalSearchFeatureEnabled = process.env.REACT_APP_GLOBAL_SEARCH_ENABLED === 'true';
  const isCustomerListFeatureEnabled = process.env.REACT_APP_CUSTOMER_LIST_ENABLED === 'true';
  const isLookalikeFeatureEnabled = process.env.REACT_APP_LOOKALIKE_ENABLED === 'true';

  switch (condition) {
    case AudienceCondition.Pixel:
      // Pixel is always available if user can create audiences (no special feature flags)
      return true;
    case AudienceCondition.Global: {
      const hasGlobalSearchAccess = isOrgAdmin || me?.organizations?.some((org) => org.globalSearchEnabled);
      return (
        isGlobalSearchFeatureEnabled &&
        (hasOrgWithSubscriptionOverride || hasOrgWithPaidSubscription) &&
        hasGlobalSearchAccess
      );
    }
    case AudienceCondition.CustomerList: {
      const hasCustomerListAccess = isOrgAdmin || me?.organizations?.some((org) => org.customerListEnabled);
      return (
        isCustomerListFeatureEnabled &&
        (hasOrgWithSubscriptionOverride || hasOrgWithPaidSubscription) &&
        hasCustomerListAccess
      );
    }
    case AudienceCondition.Lookalike: {
      const hasLookalikeAccess = isOrgAdmin || me?.organizations?.some((org) => org.lookalikeEnabled);
      return (
        isLookalikeFeatureEnabled &&
        (hasOrgWithSubscriptionOverride || hasOrgWithPaidSubscription) &&
        hasLookalikeAccess
      );
    }
    case AudienceCondition.Pulse: {
      const hasPulseAccess = isOrgAdmin || me?.organizations?.some((org) => org.pulseEnabled);
      return hasPulseAccess;
    }
    case AudienceCondition.Segment:
      // Segment creation is handled separately via canCreateSegment permission
      return false; // Always use modal for segments
    default:
      return false;
  }
};

export const getUtcDateString = (date: string | Date | Moment.Moment) => {
  if (typeof date === 'string') {
    return new Date(date).toISOString().split('T')[0];
  }
  if (Moment.isMoment(date)) {
    return date.utc().format('YYYY-MM-DD');
  }
  return date.toISOString().split('T')[0];
};

/** @deprecated Use getUtcDateString instead */
export const getDateString = getUtcDateString;

export const groupAdditionsByDate = (additions: BaseAdditionsArrayInfo[]) => {
  const groupedAdditions = additions.reduce((acc, addition) => {
    const date = getDateString(addition.date as string);
    acc[date] ||= { ...addition, matchedCount: 0 };
    acc[date].matchedCount += addition.matchedCount || 0;
    return acc;
  }, {} as Record<string, BaseAdditionsArrayInfo>);
  return groupedAdditions;
};

export const getMatchingAdditionGroupBaseOnDate = (additions: BaseAdditionsArrayInfo[], dateString: string) => {
  const groupedAdditions = groupAdditionsByDate(additions);
  return groupedAdditions[dateString];
};
